import React, { useState, useEffect, FC } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { supabase } from '../../../../config/supabaseClient';
import { FaFacebook, FaLinkedin } from 'react-icons/fa';
import toast from 'react-hot-toast';

/**
 * Type definition for social media platform connections
 */
export interface SocialAccount {
  id: string;
  platform: 'facebook' | 'linkedin' | 'instagram';
  name: string;
  connected: boolean;
  profilePicture?: string;
  accessToken?: string;
  tokenExpiry?: string;
  refreshToken?: string;
  userId: string;
}

/**
 * Social Account Connector Component
 * Manages connections to social media platforms and handles OAuth flows
 */
const SocialAccountConnector: React.FC = () => {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user } = useAuth();
  const userId = user?.id;

  // Posting default settings managed via localStorage (consumed by PostComposer)
  type UserSettings = {
    defaultFooterEnabled?: boolean;
    defaultFooterText?: string;
    defaultUrlShortenerEnabled?: boolean;
  };

  const [settings, setSettings] = useState<UserSettings>({
    defaultFooterEnabled: false,
    defaultFooterText: '',
    defaultUrlShortenerEnabled: false,
  });

  // Platforms configuration
  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <FaFacebook className="text-blue-600" size={20} />,
      color: 'bg-blue-100 hover:bg-blue-200 border-blue-300',
      textColor: 'text-blue-700'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <FaLinkedin className="text-blue-700" size={20} />,
      color: 'bg-blue-100 hover:bg-blue-200 border-blue-300',
      textColor: 'text-blue-700'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <span className="font-bold text-purple-600">Ig</span>,
      color: 'bg-purple-100 hover:bg-purple-200 border-purple-300',
      textColor: 'text-purple-700'
    }
  ];

  // Initialize and fetch connected accounts when component mounts
  useEffect(() => {
    if (userId) {
      fetchConnectedAccounts();
      
      // Check for redirect from OAuth flow
      const urlParams = new URLSearchParams(window.location.search);
      const connectionStatus = urlParams.get('connection');
      
      if (connectionStatus === 'success') {
        setSuccess('Account connected successfully!');
        toast.success('Account connected successfully!');
        // Clear the URL parameter after handling it
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (connectionStatus === 'failed') {
        const error = urlParams.get('error');
        setError(`Connection failed: ${error || 'Unknown error'}`);
        toast.error(`Connection failed: ${error || 'Unknown error'}`);
        // Clear the URL parameter after handling it
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [userId]);

  // Load persisted posting defaults
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userSettings');
      if (!raw) return;
      const s = JSON.parse(raw) || {};
      setSettings({
        defaultFooterEnabled: !!s.defaultFooterEnabled,
        defaultFooterText: s.defaultFooterText || '',
        defaultUrlShortenerEnabled: !!s.defaultUrlShortenerEnabled,
      });
    } catch (e) {
      // non-fatal
    }
  }, []);

  const saveSettings = () => {
    try {
      const raw = localStorage.getItem('userSettings');
      const existing = raw ? JSON.parse(raw) : {};
      const merged = { ...existing, ...settings };
      localStorage.setItem('userSettings', JSON.stringify(merged));
      toast.success('Posting defaults saved');
    } catch (e: any) {
      toast.error('Failed to save settings');
    }
  };

  /**
   * Connect to a social media platform
   */
  const connectAccount = (platform: string) => {
    setError('');
    if (!userId) {
      setError('You must be logged in to connect accounts.');
      return;
    }
    
    // Get OAuth redirect URL based on platform
    let authUrl = '';
    // Use backend URL for redirect URI (separate from frontend)
    const isProd = process.env.NODE_ENV === 'production';
    const backendUrl = process.env.REACT_APP_API_URL || (isProd ? 'https://api.explanatum.co.uk' : 'http://localhost:5000');
    // For Facebook/Instagram we always use the facebook callback; for LinkedIn we keep linkedin
    const resolveRedirect = (p: string) => {
      if (p === 'facebook' || p === 'instagram') {
        return `${isProd ? 'https://api.explanatum.co.uk' : backendUrl}/api/auth/facebook/callback`;
      }
      return `${backendUrl}/api/auth/${p}/callback`;
    };
    const redirectUri = resolveRedirect(platform);
    
    switch(platform) {
      case 'facebook':
      case 'instagram': {
        // Facebook Login flow (used for both Facebook Pages and Instagram Graph)
        const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || 'your-facebook-app-id';
        // Generate cryptographically-random state for CSRF protection, include platform hint
        const randomState = (window.crypto && window.crypto.getRandomValues)
          ? Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('')
          : Math.random().toString(36).slice(2);
        const composedState = `${userId}:${randomState}:${platform}`;
        sessionStorage.setItem('oauth_state', composedState);
        sessionStorage.setItem('oauth_platform', platform);
        sessionStorage.setItem('oauth_return_path', window.location.pathname + '?view=accounts');

        // Advanced scopes required for Pages and Instagram Graph
        const fbScopes = [
          'pages_show_list',
          'pages_manage_posts',
          'pages_read_engagement',
          'pages_manage_metadata',
          'pages_read_user_content',
          'instagram_basic',
          'instagram_content_publish',
          'instagram_manage_insights',
          'public_profile',
          'email',
        ].join(',');

        // Use current Graph version (v19.0 or higher)
        authUrl = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${encodeURIComponent(fbAppId)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(composedState)}&scope=${encodeURIComponent(fbScopes)}`;

        // Proceed with popup for Facebook/Instagram (same dialog)
        break;
      }
        
      case 'linkedin':
        // LinkedIn OAuth 2.0 flow
        const linkedinClientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
        const isProd = process.env.NODE_ENV === 'production';
        // Production must use explanatum.co.uk domains per org policy
        const linkedinRedirectUri = isProd
          ? 'https://api.explanatum.co.uk/api/auth/linkedin/callback'
          : 'http://localhost:5000/api/auth/linkedin/callback';

        if (!linkedinClientId) {
          toast.error('LinkedIn client ID is not configured');
          setError('LinkedIn client ID is not configured. Please set REACT_APP_LINKEDIN_CLIENT_ID.');
          return;
        }

        // Generate cryptographically-random state for CSRF protection
        const randomState = window.crypto && window.crypto.getRandomValues
          ? Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('')
          : Math.random().toString(36).slice(2);
        // Encode userId in state so backend can associate the callback with the user
        const composedState = `${userId}:${randomState}`;
        sessionStorage.setItem('oauth_state', composedState);
        sessionStorage.setItem('oauth_platform', platform);
        sessionStorage.setItem('oauth_return_path', window.location.pathname + '?view=accounts');

        // LinkedIn scopes aligned to your app's products:
        // - Sign In with LinkedIn using OpenID Connect: openid, profile, email
        // - Share on LinkedIn: w_member_social
        const scopes = ['openid', 'profile', 'email', 'w_member_social'].join('%20');

        // Build the LinkedIn auth URL (state carries userId + random suffix)
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${encodeURIComponent(linkedinClientId)}&redirect_uri=${encodeURIComponent(linkedinRedirectUri)}&state=${encodeURIComponent(composedState)}&scope=${scopes}`;
        
        console.log('LinkedIn redirect URI:', linkedinRedirectUri);
        console.log('Full LinkedIn auth URL:', authUrl);
        break;
        
      // Removed legacy Instagram Basic Display flow. Instagram uses Facebook Login above.
        
      default:
        setError(`Unsupported platform: ${platform}`);
        return;
    }
    
    // Instead of using a popup, directly navigate to the auth URL
    // This helps with LinkedIn's security restrictions on popups
    if (platform === 'linkedin') {
      console.log('Redirecting to LinkedIn auth page directly...');
      toast('Redirecting to LinkedIn login...', { icon: 'ℹ️' });
      // Store redirect info in sessionStorage
      sessionStorage.setItem('oauth_platform', platform);
      sessionStorage.setItem('oauth_return_path', window.location.pathname + '?view=accounts');
      // Direct browser redirect instead of popup
      window.location.href = authUrl;
      return;
    }
    
    // Open OAuth popup for other platforms
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;
    
    const popup = window.open(
      authUrl, 
      `Connect ${platform}`,
      `width=${width},height=${height},left=${left},top=${top}`
    );
    
    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      setError('Popup was blocked. Please allow popups for this site.');
      return;
    }
    
    // Poll to check when popup is closed
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        // Fetch accounts again to see if connection was successful
        fetchConnectedAccounts();
      }
    }, 500);
    
    // Set a timeout to clear the interval if the popup stays open too long
    setTimeout(() => {
      clearInterval(timer);
      setLoading(false);
    }, 120000); // 2 minutes timeout
  };

  // Fetch accounts data from Supabase
  const fetchConnectedAccounts = async () => {
    if (!userId) {
      setError('User must be logged in to view connected accounts');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Query the social_accounts table
      const { data, error } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('user_id', userId);
        
      if (error) throw error;
      
      if (data) {
        // Map DB results to our SocialAccount interface
        const formattedAccounts: SocialAccount[] = data.map((acc: any) => ({
          id: acc.id,
          platform: acc.platform,
          name: acc.account_name || acc.platform,
          connected: true,
          profilePicture: acc.profile_picture || undefined,
          accessToken: acc.access_token,
          tokenExpiry: acc.token_expiry,
          refreshToken: acc.refresh_token,
          userId: acc.user_id
        }));
        
        setAccounts(formattedAccounts);
      }
    } catch (err: any) {
      setError(`Error fetching connected accounts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Disconnect an already connected social media account
   */
  const disconnectAccount = async (accountId: string, platform: string) => {
    if (!userId || !accountId) return;
    
    try {
      const { error } = await supabase
        .from('social_accounts')
        .delete()
        .eq('id', accountId)
        .eq('user_id', userId);
        
      if (error) throw error;
      
      // Update local state
      setAccounts(accounts.filter(acc => acc.id !== accountId));
      setSuccess(`Successfully disconnected your ${platform} account`);
    } catch (err: any) {
      setError(`Error disconnecting account: ${err.message}`);
    }
  };

  // Note: PKCE/code_challenge should be implemented in backend if required by providers.

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Connected Accounts</h2>
        <p className="text-gray-600">Connect your social media accounts to automatically post and schedule content</p>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 relative" role="alert">
          <p>{error}</p>
          <button 
            onClick={() => setError(null)} 
            className="absolute top-0 right-0 p-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 relative" role="alert">
          <p>{success}</p>
          <button 
            onClick={() => setSuccess(null)} 
            className="absolute top-0 right-0 p-2 text-green-500 hover:text-green-700"
          >
            ×
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {platforms.map(platform => {
          // Find if this platform is already connected
          const connectedAccount = accounts.find(acc => acc.platform === platform.id);
          const isConnected = !!connectedAccount;
          
          return (
            <div 
              key={platform.id} 
              className={`border rounded-lg p-4 ${platform.color}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                    {platform.icon}
                  </div>
                  <div>
                    <h3 className={`font-medium ${platform.textColor}`}>{platform.name}</h3>
                    <p className="text-sm text-gray-600">
                      {isConnected 
                        ? `Connected as ${connectedAccount?.name}` 
                        : 'Not connected'}
                    </p>
                  </div>
                </div>
                
                <div>
                  {isConnected ? (
                    <button 
                      onClick={() => disconnectAccount(connectedAccount?.id!, platform.id)}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button 
                      onClick={() => connectAccount(platform.id)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
              
              {isConnected && (
                <div className="mt-4 text-sm text-gray-600">
                  <p>
                    {connectedAccount?.tokenExpiry && (
                      <>
                        <span className="font-medium">Token expires: </span>
                        {new Date(connectedAccount.tokenExpiry).toLocaleString()}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Posting Defaults (Footer & URL Shortener) */}
      <div className="mt-10 border rounded-lg p-6 bg-white shadow-sm">
        <h3 className="text-xl font-semibold text-gray-800 mb-1">Posting Defaults</h3>
        <p className="text-sm text-gray-600 mb-4">Configure defaults that apply when composing new posts.</p>

        <div className="space-y-6">
          <div className="flex items-start justify-between">
            <div className="mr-4">
              <label htmlFor="defaultFooterEnabled" className="block text-sm font-medium text-gray-700 mb-1">
                Append footer by default
              </label>
              <p className="text-xs text-gray-500">When enabled, your footer text is added automatically to new posts.</p>
            </div>
            <input
              id="defaultFooterEnabled"
              type="checkbox"
              className="h-5 w-5 mt-1"
              checked={!!settings.defaultFooterEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSettings(s => ({ ...s, defaultFooterEnabled: e.target.checked }))
              }
            />
          </div>

          <div>
            <label htmlFor="defaultFooterText" className="block text-sm font-medium text-gray-700 mb-1">
              Footer text
            </label>
            <textarea
              id="defaultFooterText"
              className="w-full border rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="e.g., Follow us for more updates!"
              value={settings.defaultFooterText || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setSettings(s => ({ ...s, defaultFooterText: e.target.value }))
              }
            />
            <p className="text-xs text-gray-500 mt-1">This text is appended when the footer default is enabled.</p>
          </div>

          <div className="flex items-start justify-between">
            <div className="mr-4">
              <label htmlFor="defaultUrlShortenerEnabled" className="block text-sm font-medium text-gray-700 mb-1">
                Enable URL shortener by default
              </label>
              <p className="text-xs text-gray-500">Shorten links automatically when creating posts.</p>
            </div>
            <input
              id="defaultUrlShortenerEnabled"
              type="checkbox"
              className="h-5 w-5 mt-1"
              checked={!!settings.defaultUrlShortenerEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSettings(s => ({ ...s, defaultUrlShortenerEnabled: e.target.checked }))
              }
            />
          </div>

          <div className="pt-2">
            <button
              type="button"
              onClick={saveSettings}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              Save defaults
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">Why connect your accounts?</h3>
        <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
          <li>Schedule posts across multiple platforms from one dashboard</li>
          <li>Auto-publish content at optimal times for engagement</li>
          <li>Track performance metrics and analytics in one place</li>
          <li>No need to manually copy-paste between platforms</li>
        </ul>
      </div>
    </div>
  );
};

export default SocialAccountConnector;
